import { Controller, useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { DateCalendar, MobileDatePicker } from '@mui/x-date-pickers';
import { Divider, Stack, DialogActions, Dialog, Button } from '@mui/material';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { FormProvider, RHFTextField, RHFUploadAvatar } from '@app/components';
import useResponsive from '@app/hooks/use-responsive';
import { addHolidaySchema } from '../validators';
import { IAddHolidaySchema, IHoliday } from '../types';
import { StyledHolidayPopup } from './styles';
import { addHoliday, updateHoliday } from '../slice';
import { useRequestState } from '@app/hooks';
import { selectAddHolidayState, selectUpdateHolidayState } from '../selectors';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  holiday?: IHoliday;
}

const AddHolidayPopup = ({ open, onClose, holiday }: Props) => {
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const dispatch = useDispatch();
  const isMobileView = useResponsive('down', 'sm');

  const methods = useForm<IAddHolidaySchema>({
    resolver: yupResolver(addHolidaySchema) as any,
    defaultValues: {
      name: get(holiday, 'name', ''),
      date: get(holiday, 'date', dayjs().toISOString()),
      image: get(holiday, 'image', ''),
    },
  });

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('image', newFile);
        setIsImageUploaded(true);
      }
    },
    [methods]
  );
  const { loading } = useRequestState({
    stateSelector: holiday ? selectUpdateHolidayState : selectAddHolidayState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  const onSubmit = (form: IAddHolidaySchema) => {
    holiday
      ? dispatch(updateHoliday({ ...form, id: holiday?.id, image: methods.getValues('image') }))
      : dispatch(
          addHoliday({
            ...form,
            image: methods.getValues('image'),
          })
        );
  };

  return (
    <FormProvider methods={methods}>
      <Dialog open={open} maxWidth={'md'} fullWidth>
        <Header
          variant="dialog"
          title={holiday ? Strings.holiday.updateHoliday : Strings.page_title.addHoliday}
          onClose={onClose}
        />
        <Divider />
        <StyledHolidayPopup>
          <Stack justifyContent={'center'} spacing={2} width={'100%'} m={2}>
            <RHFTextField
              name="name"
              required
              label={`${Strings.field.holidayName}`}
              variant="outlined"
            />

            <Stack sx={{ marginTop: '25px' }}>
              <RHFUploadAvatar
                name={'image'}
                onDrop={handleDrop}
                sx={{
                  borderRadius: '10px',
                  width: '100%',
                  height: 115,
                  border: 'none',
                }}
                placeholderSx={{
                  borderRadius: '10px',
                  width: '100%',
                  height: '100%',
                  border:
                    isImageUploaded || holiday?.image ? 'none' : '5px dashed rgba(0, 0, 0, 0.08)',
                }}
              />
            </Stack>
            {isMobileView && (
              <Stack>
                <Controller
                  name="date"
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <MobileDatePicker
                      value={dayjs(value)}
                      onChange={onChange}
                      sx={{
                        boxShadow: (theme) => theme.customShadows.card,
                        borderRadius: '20px',
                      }}
                    />
                  )}
                />
              </Stack>
            )}
          </Stack>

          {!isMobileView && (
            <Stack m={2}>
              <Stack justifyContent="center">
                <Controller
                  name="date"
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <DateCalendar
                      value={dayjs(value)}
                      onChange={onChange}
                      sx={{
                        boxShadow: (theme) => theme.customShadows.card,
                        borderRadius: '20px',
                      }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          )}
        </StyledHolidayPopup>
        <Divider />
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {Strings.button.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {Strings.button.save}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export { AddHolidayPopup };
